import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    DollarSign, 
    Calendar,
    Users,
    AlertTriangle,
    Settings,
    Download,
    TrendingDown,
    Clock,
    XCircle,
    CheckCircle2
} from 'lucide-react';

const PayrollPage = () => {
    const { api, hasPermission, formatCurrency } = useAuth();
    const [loading, setLoading] = useState(true);
    const [payrollData, setPayrollData] = useState(null);
    const [rules, setRules] = useState(null);
    const [services, setServices] = useState([]);
    
    // Filters
    const currentDate = new Date();
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
    const [selectedService, setSelectedService] = useState('');
    
    // Dialog
    const [showRulesDialog, setShowRulesDialog] = useState(false);
    const [rulesForm, setRulesForm] = useState({});
    
    // Permissions
    const canEditRules = hasPermission('edit_payroll_rules');

    const months = [
        { value: 1, label: 'Janvier' },
        { value: 2, label: 'Fevrier' },
        { value: 3, label: 'Mars' },
        { value: 4, label: 'Avril' },
        { value: 5, label: 'Mai' },
        { value: 6, label: 'Juin' },
        { value: 7, label: 'Juillet' },
        { value: 8, label: 'Aout' },
        { value: 9, label: 'Septembre' },
        { value: 10, label: 'Octobre' },
        { value: 11, label: 'Novembre' },
        { value: 12, label: 'Decembre' }
    ];

    const years = Array.from({ length: 3 }, (_, i) => currentDate.getFullYear() - i);

    useEffect(() => {
        fetchData();
    }, [selectedYear, selectedMonth, selectedService]);

    const fetchData = async () => {
        try {
            setLoading(true);
            let url = `/payroll/monthly/${selectedYear}/${selectedMonth}`;
            if (selectedService) url += `?service=${selectedService}`;
            
            const [payrollRes, rulesRes, servicesRes] = await Promise.all([
                api().get(url),
                api().get('/payroll/rules'),
                api().get('/services')
            ]);
            
            setPayrollData(payrollRes.data);
            setRules(rulesRes.data);
            setRulesForm(rulesRes.data);
            setServices(servicesRes.data.services);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveRules = async () => {
        try {
            await api().put('/payroll/rules', rulesForm);
            toast.success('Regles mises a jour');
            setShowRulesDialog(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const handleExport = () => {
        // Generate CSV
        const headers = ['Employe', 'Service', 'Poste', 'Salaire Base', 'Shifts', 'Presents', 'Retards', 'Absents', 'Min Retard', 'Paliers Retard', 'Heures', 'Penalite Retard', 'Penalite Absence', 'Total Deductions', 'Salaire Net'];
        const rows = payrollData?.summaries?.map(s => [
            s.staff_name,
            s.service,
            s.position,
            s.base_salary,
            s.shifts_planned,
            s.shifts_present,
            s.shifts_late,
            s.shifts_absent,
            s.total_late_minutes,
            s.total_late_penalty_hours || 0,
            s.total_work_hours,
            s.late_penalty,
            s.absence_penalty,
            s.total_deductions,
            s.net_salary
        ]) || [];
        
        const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `paie_${selectedYear}_${selectedMonth}.csv`;
        link.click();
        
        toast.success('Export CSV telecharge');
    };

    if (loading && !payrollData) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-8 bg-muted rounded w-48"></div>
                <div className="grid grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-muted rounded-xl"></div>)}
                </div>
                <div className="h-96 bg-muted rounded-xl"></div>
            </div>
        );
    }

    const totals = payrollData?.totals || {};

    return (
        <div className="space-y-6" data-testid="payroll-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold text-foreground tracking-tight">
                        Paie
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Calcul des salaires et retenues
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {canEditRules && (
                        <Button variant="outline" onClick={() => setShowRulesDialog(true)}>
                            <Settings className="w-4 h-4 mr-2" />
                            Regles
                        </Button>
                    )}
                    <Button variant="outline" onClick={handleExport}>
                        <Download className="w-4 h-4 mr-2" />
                        Export CSV
                    </Button>
                </div>
            </div>

            {/* Period Selector */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm font-medium">Periode:</span>
                        </div>
                        <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
                            <SelectTrigger className="w-36">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {months.map(m => (
                                    <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
                            <SelectTrigger className="w-24">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {years.map(y => (
                                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={selectedService || "all"} onValueChange={(v) => setSelectedService(v === "all" ? "" : v)}>
                            <SelectTrigger className="w-36">
                                <SelectValue placeholder="Tous services" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous services</SelectItem>
                                {services.map(s => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-accent/10 to-accent/5">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Employes</p>
                                <p className="text-2xl font-bold">{payrollData?.staff_count || 0}</p>
                            </div>
                            <Users className="w-8 h-8 text-accent" />
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Masse salariale</p>
                                <p className="text-xl font-bold font-mono">{totals.base_salary_formatted}</p>
                            </div>
                            <DollarSign className="w-8 h-8 text-muted-foreground" />
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-red-50">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Deductions</p>
                                <p className="text-xl font-bold font-mono text-red-600">-{totals.total_deductions_formatted}</p>
                            </div>
                            <TrendingDown className="w-8 h-8 text-red-500" />
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-green-50">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Net a payer</p>
                                <p className="text-xl font-bold font-mono text-green-600">{totals.net_salary_formatted}</p>
                            </div>
                            <CheckCircle2 className="w-8 h-8 text-green-500" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Payroll Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-accent" />
                        Detail par employe - {months.find(m => m.value === selectedMonth)?.label} {selectedYear}
                    </CardTitle>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                    {!payrollData?.summaries?.length ? (
                        <p className="text-center text-muted-foreground py-8">
                            Aucune donnee pour cette periode
                        </p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Employe</TableHead>
                                    <TableHead>Service</TableHead>
                                    <TableHead className="text-right">Base</TableHead>
                                    <TableHead className="text-center">Shifts</TableHead>
                                    <TableHead className="text-center">
                                        <CheckCircle2 className="w-4 h-4 inline text-green-500" />
                                    </TableHead>
                                    <TableHead className="text-center">
                                        <Clock className="w-4 h-4 inline text-yellow-500" />
                                    </TableHead>
                                    <TableHead className="text-center">
                                        <XCircle className="w-4 h-4 inline text-red-500" />
                                    </TableHead>
                                    <TableHead className="text-right">Min retard</TableHead>
                                    <TableHead className="text-right">Paliers</TableHead>
                                    <TableHead className="text-right">Heures</TableHead>
                                    <TableHead className="text-right">Retenue retard</TableHead>
                                    <TableHead className="text-right">Retenue absence</TableHead>
                                    <TableHead className="text-right">Total deduc.</TableHead>
                                    <TableHead className="text-right font-bold">Net</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {payrollData.summaries.map(summary => (
                                    <TableRow key={summary.staff_id}>
                                        <TableCell className="font-medium">{summary.staff_name}</TableCell>
                                        <TableCell>
                                            <Badge variant="outline" className="text-xs">
                                                {summary.service}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right font-mono">
                                            {summary.base_salary_formatted}
                                        </TableCell>
                                        <TableCell className="text-center">{summary.shifts_planned}</TableCell>
                                        <TableCell className="text-center text-green-600">{summary.shifts_present}</TableCell>
                                        <TableCell className="text-center text-yellow-600">{summary.shifts_late}</TableCell>
                                        <TableCell className="text-center text-red-600">
                                            {summary.shifts_absent + summary.shifts_not_clocked}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {summary.total_late_minutes > 0 && (
                                                <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
                                                    {summary.total_late_minutes} min
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {(summary.total_late_penalty_hours || 0) > 0 && (
                                                <Badge variant="outline" className="bg-orange-50 text-orange-700">
                                                    {summary.total_late_penalty_hours}h
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right font-mono">
                                            {summary.total_work_hours}h
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-red-600">
                                            {summary.late_penalty > 0 ? `-${summary.late_penalty_formatted}` : '-'}
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-red-600">
                                            {summary.absence_penalty > 0 ? `-${summary.absence_penalty_formatted}` : '-'}
                                        </TableCell>
                                        <TableCell className="text-right font-mono font-semibold text-red-600">
                                            {summary.total_deductions > 0 ? `-${summary.total_deductions_formatted}` : '-'}
                                        </TableCell>
                                        <TableCell className="text-right font-mono font-bold text-green-600">
                                            {summary.net_salary_formatted}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Rules Info Card */}
            <Card className="bg-muted/30">
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Settings className="w-4 h-4" />
                        Regles de calcul en vigueur
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                            <p className="text-muted-foreground">Tolerance retard</p>
                            <p className="font-medium">{rules?.tardiness_grace_minutes || 10} min</p>
                        </div>
                        <div>
                            <p className="text-muted-foreground">Penalite/palier (1h)</p>
                            <p className="font-medium">{formatCurrency(rules?.late_penalty_per_hour || 3000)}</p>
                        </div>
                        <div>
                            <p className="text-muted-foreground">Mode absence</p>
                            <p className="font-medium">{rules?.absence_penalty_mode === 'FULL_DAY' ? 'Journee complete' : 'Demi-journee'}</p>
                        </div>
                        <div>
                            <p className="text-muted-foreground">Jours/mois</p>
                            <p className="font-medium">{rules?.working_days_per_month || 26} jours</p>
                        </div>
                    </div>
                    <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                        <strong>Calcul retard:</strong> Au-dela de {rules?.tardiness_grace_minutes || 10} min, paliers d'1 heure.
                        Ex: 11 min = 1 palier = {formatCurrency(rules?.late_penalty_per_hour || 3000)}, 
                        61 min = 2 paliers = {formatCurrency((rules?.late_penalty_per_hour || 3000) * 2)}
                    </div>
                </CardContent>
            </Card>

            {/* Rules Dialog */}
            <Dialog open={showRulesDialog} onOpenChange={setShowRulesDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Configuration des regles de paie</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Tolerance retard (min)</Label>
                                <Input 
                                    type="number"
                                    value={rulesForm.tardiness_grace_minutes || ''}
                                    onChange={(e) => setRulesForm({...rulesForm, tardiness_grace_minutes: Number(e.target.value)})}
                                />
                                <p className="text-xs text-muted-foreground">Defaut: 10 minutes</p>
                            </div>
                            <div className="space-y-2">
                                <Label>Jours travailles/mois</Label>
                                <Input 
                                    type="number"
                                    value={rulesForm.working_days_per_month || ''}
                                    onChange={(e) => setRulesForm({...rulesForm, working_days_per_month: Number(e.target.value)})}
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Penalite par palier d'1 heure (CFA)</Label>
                            <Input 
                                type="number"
                                value={rulesForm.late_penalty_per_hour || ''}
                                onChange={(e) => setRulesForm({...rulesForm, late_penalty_per_hour: Number(e.target.value)})}
                            />
                            <p className="text-xs text-muted-foreground">
                                Defaut: 3 000 CFA. Ex: 11-60 min = 1 palier, 61-120 min = 2 paliers
                            </p>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Mode penalite absence</Label>
                            <Select 
                                value={rulesForm.absence_penalty_mode || 'FULL_DAY'} 
                                onValueChange={(v) => setRulesForm({...rulesForm, absence_penalty_mode: v})}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="FULL_DAY">Journee complete</SelectItem>
                                    <SelectItem value="HALF_DAY">Demi-journee</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Seuil "non pointe" (min apres debut)</Label>
                            <Input 
                                type="number"
                                value={rulesForm.not_clocked_threshold_minutes || ''}
                                onChange={(e) => setRulesForm({...rulesForm, not_clocked_threshold_minutes: Number(e.target.value)})}
                            />
                            <p className="text-xs text-muted-foreground">
                                Defaut: 60 min. Apres ce delai sans pointage = Absent
                            </p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRulesDialog(false)}>
                            Annuler
                        </Button>
                        <Button onClick={handleSaveRules}>
                            Enregistrer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PayrollPage;
